import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { UploadKind } from '@photobooth/shared';

import styles from '../styles/app.module.css';
import { Button } from '../ui/Button';
import { Toast } from '../ui/Toast';
import { FooterCredit } from '../ui/FooterCredit';
import { shareMedia, shareOrCopy } from '../utils/shareLink';

interface LocationState {
  mediaUrl?: string;
  shareUrl?: string;
}

interface Props {
  kind: UploadKind;
}

function reconstructMediaUrl(id: string, kind: UploadKind): string | null {
  const base = import.meta.env.VITE_API_BASE_URL;
  if (!base) return null;
  // The API exposes the share-url canonical, but we don't know the bucket-host
  // here without a request. We attempt a sensible default: <base>/media/<id>.<ext>.
  // If that URL 404s, the user can still navigate back to /.
  const ext = kind === 'photo' ? 'jpg' : 'mp4';
  return `${base.replace(/\/+$/, '')}/media/${id}.${ext}`;
}

export function SharePage({ kind }: Props) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as LocationState;
  const [toast, setToast] = useState<string | null>(null);

  const mediaUrl = state.mediaUrl ?? (id ? reconstructMediaUrl(id, kind) : null);
  const shareUrl =
    state.shareUrl ??
    (typeof window !== 'undefined' ? window.location.href : '');

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const [sharing, setSharing] = useState(false);

  // Native share sheet with the media attached. iOS/Android show
  // Instagram / X / TikTok / Messages / AirDrop directly. Falls back to
  // URL-only share on browsers that don't support file shares, and then
  // to clipboard.
  const doSharePhoto = useCallback(async () => {
    if (!mediaUrl) return;
    setSharing(true);
    try {
      // Pull the real extension off the media URL since video can be either
      // mp4 or webm depending on what the browser produced at capture time.
      const m = mediaUrl.match(/\.([a-z0-9]{3,4})(?:\?|#|$)/i);
      const urlExt = (m?.[1] ?? '').toLowerCase();
      const ext = kind === 'photo' ? 'jpg' : urlExt === 'webm' ? 'webm' : 'mp4';
      const contentType =
        kind === 'photo'
          ? 'image/jpeg'
          : ext === 'webm'
            ? 'video/webm'
            : 'video/mp4';
      const result = await shareMedia({
        title: 'My Photobooth shot',
        text: 'From photobooth.ofunc.io',
        url: shareUrl,
        mediaUrl,
        filename: `photobooth-${id ?? Date.now()}.${ext}`,
        contentType,
      });
      if (result.kind === 'copied') setToast('Link copied to clipboard');
      else if (result.kind === 'shared') setToast('Shared!');
      else if (result.kind === 'error') setToast(result.message);
    } finally {
      setSharing(false);
    }
  }, [mediaUrl, kind, shareUrl, id]);

  const doShareLink = useCallback(async () => {
    const result = await shareOrCopy({
      title: 'My Photobooth shot',
      url: shareUrl,
    });
    if (result.kind === 'copied') setToast('Link copied to clipboard');
    else if (result.kind === 'shared') setToast('Shared!');
    else if (result.kind === 'error') setToast(result.message);
  }, [shareUrl]);

  if (!id) {
    return (
      <div className={styles.sharePage}>
        <h1>Missing share id</h1>
        <Button variant="primary" onClick={() => navigate('/')}>
          Back to booth
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.sharePage}>
      <h1 style={{ margin: 0 }}>Looking good</h1>
      {mediaUrl && (
        <>
          {kind === 'photo' ? (
            <img
              src={mediaUrl}
              alt="Your photobooth shot"
              className={styles.previewMedia}
            />
          ) : (
            <video
              src={mediaUrl}
              controls
              playsInline
              autoPlay
              className={styles.previewMedia}
            />
          )}
        </>
      )}
      <div className={styles.row}>
        {mediaUrl && (
          <Button
            variant="primary"
            disabled={sharing}
            onClick={() => void doSharePhoto()}
          >
            {sharing
              ? 'Preparing…'
              : kind === 'photo'
                ? 'Share photo'
                : 'Share video'}
          </Button>
        )}
        <Button variant="secondary" onClick={() => void doShareLink()}>
          Copy link
        </Button>
        {mediaUrl && (
          <Button
            variant="ghost"
            onClick={() => {
              const a = document.createElement('a');
              a.href = mediaUrl;
              a.download = `photobooth-${id}.${kind === 'photo' ? 'jpg' : 'mp4'}`;
              a.target = '_blank';
              a.rel = 'noopener noreferrer';
              document.body.appendChild(a);
              a.click();
              a.remove();
            }}
          >
            Download
          </Button>
        )}
        <Button variant="ghost" onClick={() => navigate('/')}>
          Take another
        </Button>
      </div>
      <code style={{ color: 'var(--text-dim)', fontSize: 12 }}>{shareUrl}</code>
      {toast && <Toast message={toast} />}
      <FooterCredit />
    </div>
  );
}
